import { Check } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/utils/cn'
import { STEP_LABELS, type StepKey } from '../wizard'

/** Numbered steps; the completed ones can be clicked to go back and change them. */
export function StepProgress({
  steps,
  current,
  reached,
  onGo,
}: {
  steps: StepKey[]
  current: number
  /** Furthest step reached so far. */
  reached: number
  onGo: (index: number) => void
}) {
  const { t } = useTranslation('admissions')
  const currentKey = steps[current] ?? 'class'
  return (
    <nav aria-label={t('Application steps')} className="min-w-0">
      <p className="mb-2 text-xs font-medium text-muted sm:sr-only">
        {t('Step {{n}} of {{total}}: {{name}}', {
          n: current + 1,
          total: steps.length,
          name: t(STEP_LABELS[currentKey]),
        })}
      </p>
      <ol className="flex items-center gap-1">
        {steps.map((key, i) => {
          const done = i < current || (i <= reached && i !== current)
          const active = i === current
          const clickable = i <= reached && !active
          const content = (
            <>
              <span
                className={cn(
                  'grid size-7 shrink-0 place-items-center rounded-full text-xs font-semibold ring-1 transition-colors',
                  active && 'bg-brand-600 text-white ring-brand-600',
                  done && !active && 'bg-brand-50 text-brand-700 ring-brand-200',
                  !done && !active && 'bg-surface text-muted ring-line',
                )}
              >
                {done && !active ? <Check className="size-3.5" aria-hidden="true" /> : i + 1}
              </span>
              <span
                className={cn(
                  'hidden min-w-0 truncate text-xs font-medium sm:inline',
                  active ? 'text-ink' : 'text-muted',
                )}
              >
                {t(STEP_LABELS[key])}
              </span>
            </>
          )
          return (
            <li key={key} className="flex min-w-0 flex-1 items-center gap-1">
              {clickable ? (
                <button
                  type="button"
                  onClick={() => onGo(i)}
                  className="flex min-w-0 items-center gap-1.5 rounded-full py-0.5 pr-1 hover:bg-surface-sunken"
                  aria-label={t('Go back to step {{n}}: {{name}}', { n: i + 1, name: t(STEP_LABELS[key]) })}
                >
                  {content}
                </button>
              ) : (
                <span
                  className="flex min-w-0 items-center gap-1.5"
                  aria-current={active ? 'step' : undefined}
                >
                  {content}
                </span>
              )}
              {i < steps.length - 1 ? (
                <span
                  aria-hidden="true"
                  className={cn('h-px min-w-2 flex-1', i < reached ? 'bg-brand-300' : 'bg-line')}
                />
              ) : null}
            </li>
          )
        })}
      </ol>
    </nav>
  )
}
